/**
 * MessageInput is the main component for composing messages and emails.
 * It orchestrates multiple subcomponents to provide a complete message composition experience:
 * - EmailFields: Handles email-specific fields (To, CC, Subject)
 * - TiptapEditor: Rich text editor for message content
 * - TemplateDialog: Template selection and insertion
 * - MessageToolbar: Actions like emoji, attachments, and send
 *
 * ## Content Preservation Strategy
 * This component follows a "never destroy user content" principle:
 * - `userContent` stores ONLY what the user has typed (no forward metadata)
 * - `editorContent` is the display content (userContent + mode-specific metadata)
 * - When switching modes, userContent is always preserved
 * - Forward headers are added/removed dynamically based on mode
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Content, Editor } from '@tiptap/react';
import { toast } from 'sonner';
import type { EmailChipsInputHandle } from '@/components/email-recipient-input';
import { TiptapEditor } from '@/components/tiptap';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeal } from '@/contexts/deal-context';
import { useHub } from '@/contexts/hub-context';
import { useEmailTemplates, useRenderEmailTemplate } from '@/hooks/use-email-templates';
import { useUserTemplates } from '@/hooks/use-user-templates';
import { convertToEmailHtml, createForwardedEmailHeader, extractUserContentFromForward, createReplyEmailContext, extractUserContentFromReply } from '@/lib/email';
import { VERIFY_EMAIL } from '@/lib/globals';
import { parseParticipants } from '@/lib/threads';
import { getLastItem } from '@/lib/utils';
import type { FormValues, MessageInputProps } from './types';
import type { SendMode } from '../../types/communications';
import { EmailFields } from './email-fields';
import { MessageToolbar } from './message-toolbar';
import { TemplateDialog } from './template-dialog';

/**
 * Strips trailing <br> and <br/> tags from content
 */
function stripTrailingLineBreaks(content: string): string {
  return content.replaceAll(/<br\s*\/?>\s*$/gi, '').trim();
}

/**
 * Builds the display content by combining user content with mode-specific metadata.
 * - For forward mode: appends forwarded message header and body
 * - For reply modes: appends reply context with quoted original message
 * - For other modes: returns user content as-is
 */
function buildDisplayContent(
  userContent: string,
  replyMessage: Parameters<typeof createForwardedEmailHeader>[0] | undefined,
  replyMode: 'reply' | 'reply-all' | 'forward' | undefined,
): string {
  if (replyMode === 'forward' && replyMessage) {
    const forwardedHeader = createForwardedEmailHeader(replyMessage);
    const originalBody = 'content' in replyMessage
      ? (replyMessage as { content?: { body?: string } }).content?.body || ''
      : '';

    // Strip any trailing line breaks from user content before adding separator
    const cleanUserContent = stripTrailingLineBreaks(userContent);

    if (cleanUserContent) {
      return `${cleanUserContent}<br><br>${forwardedHeader}${originalBody}`;
    }

    // No user content: insert a single empty paragraph above the forwarded header
    return `<p class="text-node"></p>${forwardedHeader}${originalBody}`;
  }

  if ((replyMode === 'reply' || replyMode === 'reply-all') && replyMessage) {
    const replyContext = createReplyEmailContext(replyMessage);

    // Strip any trailing line breaks from user content before adding separator
    const cleanUserContent = stripTrailingLineBreaks(userContent);

    if (cleanUserContent) {
      return `${cleanUserContent}${replyContext}`;
    }

    // No user content: insert a single empty paragraph above the reply header
    return `<p class="text-node"></p>${replyContext}`;
  }

  return userContent;
}

/**
 * Builds the email subject based on mode and existing subject.
 * - Forward mode: adds "Fwd:" prefix if not present
 * - Reply modes: adds "Re:" prefix if not present
 */
function buildSubject(
  baseSubject: string,
  mode: 'reply' | 'reply-all' | 'forward' | undefined,
): string {
  if (!baseSubject)
    return '';

  const lowerSubject = baseSubject.toLowerCase();

  if (mode === 'forward') {
    if (lowerSubject.startsWith('fwd:'))
      return baseSubject;
    // Remove Re: prefix if present before adding Fwd:
    const withoutRe = lowerSubject.startsWith('re:')
      ? baseSubject.slice(3).trim()
      : baseSubject;
    return `Fwd: ${withoutRe}`;
  }

  if (mode === 'reply' || mode === 'reply-all') {
    if (lowerSubject.startsWith('re:'))
      return baseSubject;
    // Remove Fwd: prefix if present before adding Re:
    const withoutFwd = lowerSubject.startsWith('fwd:')
      ? baseSubject.slice(4).trim()
      : baseSubject;
    return `Re: ${withoutFwd}`;
  }

  return baseSubject;
}

/**
 * Main message composition component that coordinates all messaging functionality
 * Features:
 * - Email mode with recipient management
 * - Rich text editing
 * - Template support
 * - File attachments
 * - Reply handling with content preservation across mode switches
 */
export function MessageInput({ onSend, onUploadFiles }: MessageInputProps) {
  const {
    replyThread,
    replyMessage,
    replyMode,
    setReplyThread,
    myGrantEmail,
    verifyUserId,
    sendAsGrantId,
    sendAsEmailAddress,
    replyKey,
  } = useHub();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isEmailMode, setIsEmailMode] = useState(true);
  const [showCc, setShowCc] = useState(true);
  const [showBcc, setShowBcc] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [sendMode, setSendMode] = useState<SendMode>('send');
  const [attachments, setAttachments] = useState<File[]>([]);

  // Core content state: userContent is NEVER destroyed except on send
  // This stores only user-typed content, without any forward metadata
  const [userContent, setUserContent] = useState<string>('');

  // Track the previous mode and message to detect changes
  const prevModeRef = useRef<typeof replyMode>(undefined);
  const prevMessageIdRef = useRef<string | undefined>(undefined);

  const { template } = useUserTemplates({
    name: 'personal-footer',
    userId: sendAsEmailAddress === VERIFY_EMAIL ? verifyUserId : undefined,
  });
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showHtmlDebug, setShowHtmlDebug] = useState(false);
  const [debugHtmlContent, setDebugHtmlContent] = useState('');
  const [editorContent, setEditorContent] = useState<Content>('');
  const { dealId } = useDeal();
  const [selectedTemplate, setSelectedTemplate] = useState<string>();
  const [lastAppliedTemplate, setLastAppliedTemplate] = useState<string>();
  const editorRef = useRef<Editor | null>(null);
  const emailInputRef = useRef<EmailChipsInputHandle>(null);
  const ccInputRef = useRef<EmailChipsInputHandle>(null);
  const bccInputRef = useRef<EmailChipsInputHandle>(null);

  const { data: templates } = useEmailTemplates();

  const { control, watch } = useForm<FormValues>({
    defaultValues: {
      recipients: [],
      ccRecipients: [],
      bccRecipients: [],
    },
  });

  // Watch recipient fields for validation and template rendering
  const recipients = watch('recipients');
  const ccRecipients = watch('ccRecipients');
  const bccRecipients = watch('bccRecipients');

  const {
    data: renderedTemplate,
    isLoading: isRenderingTemplate,
    error: renderError,
  } = useRenderEmailTemplate(selectedTemplate ?? '', dealId ?? '', recipients, ccRecipients);

  // Handle mode and message changes - rebuild display content while preserving user content
  useEffect(() => {
    if (!replyThread && !replyMessage) {
      // No reply context - keep user content, clear mode-specific state
      prevModeRef.current = undefined;
      prevMessageIdRef.current = undefined;
      return;
    }

    setIsEmailMode(true);

    const previousMode = prevModeRef.current;
    const currentMessageId = replyMessage?.message_id;

    // Get the base subject from the message or thread
    const baseSubject = replyMessage?.content?.subject || replyThread?.subject || '';

    // Update subject based on mode
    const newSubject = buildSubject(baseSubject, replyMode);
    setEmailSubject(newSubject);

    // Get current user content from editor (extract if coming from forward/reply mode)
    let currentUserContent = userContent;
    if (editorRef.current) {
      const editorHtml = editorRef.current.getHTML();
      // If we were in forward mode, extract just the user portion
      if (previousMode === 'forward' && editorHtml) {
        currentUserContent = extractUserContentFromForward(editorHtml);
      } else if ((previousMode === 'reply' || previousMode === 'reply-all') && editorHtml) {
        currentUserContent = extractUserContentFromReply(editorHtml);
      } else if (editorHtml) {
        currentUserContent = editorHtml;
      }
    }

    // Update userContent state if it changed
    if (currentUserContent !== userContent) {
      setUserContent(currentUserContent);
    }

    // Build the display content based on mode
    const displayContent = buildDisplayContent(currentUserContent, replyMessage, replyMode);

    // Update editor with new display content
    setEditorContent(displayContent);
    if (editorRef.current) {
      editorRef.current.commands.setContent(displayContent);
    }

    // Update refs for next comparison
    prevModeRef.current = replyMode;
    prevMessageIdRef.current = currentMessageId;
  }, [replyThread, replyMessage, replyMode, replyKey]);

  // Populate email addresses from reply thread or message after a small delay
  // to ensure components are mounted
  useEffect(() => {
    if (replyThread && isEmailMode && replyThread.participants) {
      const timer = setTimeout(() => {
        emailInputRef.current?.clear();
        ccInputRef.current?.clear();
        bccInputRef.current?.clear();

        // For forward mode, leave recipients empty
        if (replyMode === 'forward') {
          return;
        }

        const messageToEmailAddresses = parseParticipants([
          ...(replyMessage?.from ?? []),
          ...(replyMessage?.to ?? []),
        ])
          .map((participant) => participant.email)
          .filter((email): email is string => Boolean(email) && email !== myGrantEmail);

        const messageCCEmailAddresses = parseParticipants(replyMessage?.cc ?? [])
          .map((participant) => participant.email)
          .filter((email): email is string => Boolean(email) && email !== myGrantEmail);

        const messageBccEmailAddresses = parseParticipants(replyMessage?.bcc ?? [])
          .map((participant) => participant.email)
          .filter((email): email is string => Boolean(email) && email !== myGrantEmail);

        const threadEmailAddresses = parseParticipants(replyThread.participants)
          .map((participant) => participant.email)
          .filter((email): email is string => Boolean(email) && email !== myGrantEmail);

        if (emailInputRef.current) {
          if (!messageToEmailAddresses && !messageCCEmailAddresses) {
            for (const email of threadEmailAddresses) {
              emailInputRef.current?.addEmail(email);
            }
          } else {
            // For 'reply' mode, only add sender to To field
            if (replyMode === 'reply') {
              for (const email of messageToEmailAddresses) {
                emailInputRef.current?.addEmail(email);
              }
            } else {
              // For 'reply-all' mode, add all recipients
              for (const email of messageToEmailAddresses) {
                emailInputRef.current?.addEmail(email);
              }

              if (ccInputRef.current) {
                for (const email of messageCCEmailAddresses) {
                  ccInputRef.current?.addEmail(email);
                }
              }

              if (bccInputRef.current) {
                for (const email of messageBccEmailAddresses) {
                  bccInputRef.current?.addEmail(email);
                }
              }
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [replyThread, replyMessage, isEmailMode, myGrantEmail, replyKey, replyMode]);

  // Handle editor content changes - always extract and preserve user content
  const handleContentChange = useCallback((content: Content) => {
    const contentStr = typeof content === 'string' ? content : content?.toString() || '';

    // Always extract user content (strip forward/reply metadata if present)
    // This ensures userContent only ever contains what the user typed
    let extractedUserContent: string;
    if (replyMode === 'forward') {
      extractedUserContent = extractUserContentFromForward(contentStr);
    } else if (replyMode === 'reply' || replyMode === 'reply-all') {
      extractedUserContent = extractUserContentFromReply(contentStr);
    } else {
      extractedUserContent = contentStr;
    }

    setUserContent(extractedUserContent);
    setEditorContent(content);
  }, [replyMode]);

  // Handle sending the message, including email-specific data when in email mode
  const handleSend = useCallback((sendAt?: number) => {
    if (!editorContent)
      return;

    const html = typeof editorContent === 'string' ? editorContent : editorContent.toString();
    const emailHtml = convertToEmailHtml(html);
    const content = {
      html: emailHtml,
      isEmail: isEmailMode,
      templateName: lastAppliedTemplate,
      ...(isEmailMode && {
        sendAt,
        to: recipients,
        cc: ccRecipients,
        bcc: bccRecipients,
        subject: emailSubject,
        attachments: attachments.length > 0 ? attachments : undefined,
        // Only include thread info for replies, not forwards (forwards start new threads in Gmail)
        ...(replyMode !== 'forward' && {
          threadGrantId: replyThread?.grant_id,
          replyToMessageId: getLastItem(replyThread?.message_ids),
        }),
        sendAsGrantId,
      }),
    };

    onSend(content);

    if (isEmailMode && replyThread?.message_ids) {
      setReplyThread(undefined, undefined);
    }

    // Clear all content after sending
    setUserContent('');
    setEditorContent('');
    if (editorRef.current) {
      editorRef.current.commands.clearContent();
    }

    if (isEmailMode) {
      emailInputRef.current?.clear();
      ccInputRef.current?.clear();
      bccInputRef.current?.clear();
      setEmailSubject('');
      setAttachments([]);
    }

    // Clear refs after sending
    prevModeRef.current = undefined;
    prevMessageIdRef.current = undefined;
    setLastAppliedTemplate(undefined);
  }, [
    editorContent,
    onSend,
    setReplyThread,
    replyThread,
    replyMode,
    isEmailMode,
    recipients,
    ccRecipients,
    bccRecipients,
    emailSubject,
    sendAsGrantId,
    lastAppliedTemplate,
    attachments,
  ]);

  // Handle file attachment selection and upload
  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = [...e.target.files || []];
      if (files.length > 0 && onUploadFiles) {
        onUploadFiles(files);
        e.target.value = '';
      }
    },
    [onUploadFiles],
  );

  // Insert selected emoji at cursor position in editor
  const insertEmoji = useCallback((emoji: any) => {
    if (editorRef.current) {
      editorRef.current.commands.insertContent(emoji.native);
    }
    setShowEmojiPicker(false);
  }, []);

  // Handle template selection and trigger template rendering
  const handleTemplateSelect = useCallback((template: { name: string }) => {
    setShowTemplateDialog(false);
    setSelectedTemplate(template.name);
  }, []);

  // Update editor content and email fields when template renders successfully
  useEffect(() => {
    if (renderedTemplate && editorRef.current && selectedTemplate) {
      editorRef.current.commands.setContent(renderedTemplate.html);
      setEditorContent(renderedTemplate.html);
      // Template content becomes the new user content
      setUserContent(renderedTemplate.html);

      if (isEmailMode) {
        setEmailSubject(renderedTemplate.subject);
        if (renderedTemplate.to) {
          for (const email of renderedTemplate.to) {
            emailInputRef.current?.addEmail(email);
          }
        }
        if (renderedTemplate.cc) {
          for (const email of renderedTemplate.cc) {
            ccInputRef.current?.addEmail(email);
          }
        }
      }

      // Track the successfully applied template
      setLastAppliedTemplate(selectedTemplate);
      setSelectedTemplate(undefined);
    }
  }, [renderedTemplate, isEmailMode, selectedTemplate]);

  // Display error message if template rendering fails and reset selection
  useEffect(() => {
    if (renderError) {
      toast.error('Failed to render template', {
        description: renderError.message,
      });
      setSelectedTemplate(undefined);
    }
  }, [renderError]);

  // Insert special character at cursor position in editor
  const handleSpecialCharacter = useCallback((char: string) => {
    if (editorRef.current) {
      editorRef.current.commands.insertContent(char);
    }
  }, []);

  // Insert user's signature at cursor position in editor
  const handleSignatureInsert = useCallback((userSignature: string) => {
    if (editorRef.current) {
      editorRef.current.commands.insertContent(userSignature);
    }
  }, []);

  // Store reference to editor instance for direct manipulation
  const handleEditorChange = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  // Determine if send button should be enabled based on content and mode
  const isSendEnabled = useCallback((): boolean => {
    if (!editorContent)
      return false;
    if (!isEmailMode)
      return true;
    if (!emailSubject)
      return false;

    return Boolean(recipients && recipients.length > 0);
  }, [editorContent, isEmailMode, emailSubject, recipients]);

  // Debug: Show raw HTML content in dialog
  const handleShowHtml = useCallback(() => {
    const html = editorRef.current?.getHTML() || '';
    setDebugHtmlContent(html);
    setShowHtmlDebug(true);
    // Also log to console for easy copying
    console.log('=== Editor HTML Debug ===');
    console.log('Raw HTML:', html);
    console.log('User Content:', userContent);
    console.log('Reply Mode:', replyMode);
    console.log('========================');
  }, [userContent, replyMode]);

  return (
    <div className="bg-background">
      {isEmailMode && (
        <EmailFields
          control={control}
          emailSubject={emailSubject}
          setEmailSubject={setEmailSubject}
          showCc={showCc}
          setShowCc={setShowCc}
          emailInputRef={emailInputRef}
          ccInputRef={ccInputRef}
          bccInputRef={bccInputRef}
          showBcc={showBcc}
          setShowBcc={setShowBcc}
        />
      )}

      <div className="rounded-md border bg-background">
        <TiptapEditor
          value={editorContent}
          onChange={handleContentChange}
          placeholder="Type a message..."
          className="min-h-[250px]"
          hideSections={['one', 'tasks']}
          disableAutolink
          editorContentClassName="prose prose-sm max-w-none focus:outline-none min-h-[40px] max-h-[400px] overflow-y-auto px-3 py-2"
          onCreate={({ editor }) => handleEditorChange(editor)}
          isEmailMode={isEmailMode}
          onEmailModeChange={(mode) => {
            setIsEmailMode(mode);
            if (!mode && replyThread) {
              setReplyThread(undefined);
            }
          }}
          onTemplateClick={() => setShowTemplateDialog(true)}
        />

        <MessageToolbar
          onEmojiSelect={insertEmoji}
          onFileUpload={handleFileUpload}
          onSpecialCharacter={handleSpecialCharacter}
          onSignatureInsert={handleSignatureInsert}
          onSend={handleSend}
          isSendEnabled={isSendEnabled()}
          showEmojiPicker={showEmojiPicker}
          setShowEmojiPicker={setShowEmojiPicker}
          template={template ? { template: template.template } : undefined}
          sendMode={sendMode}
          onSendModeChange={setSendMode}
          onShowHtml={handleShowHtml}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
        />
      </div>

      <TemplateDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        onTemplateSelect={handleTemplateSelect}
        templates={templates}
        isLoading={isRenderingTemplate}
        selectedTemplate={selectedTemplate}
      />

      {/* HTML Debug Dialog */}
      {import.meta.env.DEV && (
        <Dialog open={showHtmlDebug} onOpenChange={setShowHtmlDebug}>
          <DialogContent className="max-h-[80vh] max-w-3xl">
            <DialogHeader>
              <DialogTitle>HTML Debug View</DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-auto">
              <pre className="whitespace-pre-wrap break-all rounded bg-muted p-4 font-mono text-xs">
                {debugHtmlContent}
              </pre>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
